/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetRiskUserBreakdownRequest,
  GetRiskUserBreakdownSecurity,
} from "../models/operations/getriskuserbreakdown.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskUserBreakdownQuery,
  prefetchRiskUserBreakdown,
  queryKeyRiskUserBreakdown,
  RiskUserBreakdownQueryData,
} from "./riskUserBreakdown.core.js";
export {
  buildRiskUserBreakdownQuery,
  prefetchRiskUserBreakdown,
  queryKeyRiskUserBreakdown,
  type RiskUserBreakdownQueryData,
};

export type RiskUserBreakdownQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getRiskUserBreakdown risk
 *
 * @remarks
 * Per-user breakdowns of findings by category and by rule_id within a time window. Powers the risk panels on identity pages.
 */
export function useRiskUserBreakdown(
  request: GetRiskUserBreakdownRequest,
  security?: GetRiskUserBreakdownSecurity | undefined,
  options?: QueryHookOptions<
    RiskUserBreakdownQueryData,
    RiskUserBreakdownQueryError
  >,
): UseQueryResult<RiskUserBreakdownQueryData, RiskUserBreakdownQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskUserBreakdownQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getRiskUserBreakdown risk
 *
 * @remarks
 * Per-user breakdowns of findings by category and by rule_id within a time window. Powers the risk panels on identity pages.
 */
export function useRiskUserBreakdownSuspense(
  request: GetRiskUserBreakdownRequest,
  security?: GetRiskUserBreakdownSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskUserBreakdownQueryData,
    RiskUserBreakdownQueryError
  >,
): UseSuspenseQueryResult<
  RiskUserBreakdownQueryData,
  RiskUserBreakdownQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskUserBreakdownQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskUserBreakdownData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      externalUserId: string;
      from?: Date | undefined;
      to?: Date | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskUserBreakdownQueryData,
): RiskUserBreakdownQueryData | undefined {
  const key = queryKeyRiskUserBreakdown(...queryKeyBase);

  return client.setQueryData<RiskUserBreakdownQueryData>(key, data);
}

export function invalidateRiskUserBreakdown(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      externalUserId: string;
      from?: Date | undefined;
      to?: Date | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "overview", "userBreakdown", ...queryKeyBase],
  });
}

export function invalidateAllRiskUserBreakdown(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "overview", "userBreakdown"],
  });
}
